import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion, type DragControls } from "motion/react";
import {ChevronDown,ClipboardCheck,FileText,History,Lightbulb,Maximize2,Minimize2,Minus,PieChart,RotateCcw,Send,ShieldCheck,Sparkles,Square,X,type LucideIcon,} from "lucide-react";
import { useLocation } from "react-router";
import { cn } from "../../ui/utils";
import { AssistantMascot } from "./AssistantMascot";
import { HistoryBanner, HistoryPanel } from "./HistoryPanel";
import { MessageBubble, TypingIndicator } from "./MessageBubble";
import { SuggestionChip, SuggestionRowSkeleton } from "./SuggestionCard";
import { TranscriptSeparator } from "./TranscriptSeparator";
import { Switch } from "../../ui/switch";
import { panelPageLabel } from "../lib/pageLabel";
import { useComplianceAssistantStore } from "../store";
import { buildTranscriptItems } from "../lib/transcript";
import { DEFAULT_GREETING } from "../lib/greeting";
import { useUiContextStore } from "@/stores/uiContextStore";
import type { AssistantSuggestionOrigin, ChatMessage } from "../types";

const MAX_CHARS = 1000;

function HeaderButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className="flex size-11 cursor-pointer items-center justify-center rounded-lg text-white/80 transition-[color,background-color,transform] duration-150 hover:bg-white/15 hover:text-white active:scale-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 motion-reduce:active:scale-100 sm:size-8"
    >
      {children}
    </button>
  );
}

/**
 * What the assistant covers, shown under "Ask about".
 *
 * The symbols deliberately match the ones `SuggestionCard`'s `ICON_RULES` picks for these same words, so a
 * "Controls" chip here and a control-flavoured suggestion row below it carry the same icon.
 */
const SCOPE_AREAS: Array<{ icon: LucideIcon; label: string }> = [
  { icon: ShieldCheck, label: "Controls" },
  { icon: FileText, label: "Frameworks" },
  { icon: ClipboardCheck, label: "Assessments" },
  { icon: PieChart, label: "Compliance" },
];

// RegO branding lockup under the composer. The gradient ids are `ca-` prefixed to keep
// them from colliding with any other inline SVG on the page.
function PoweredByRego() {
  return (
    <span className="flex items-center gap-1 text-[0.6875rem] font-medium text-muted-foreground">
      Powered by
      <svg
        width="14"
        height="14"
        viewBox="0 0 32 32"
        fill="none"
        aria-hidden
        className="shrink-0"
      >
        <defs>
          <linearGradient id="ca-shield-grad-1" x1="0%" y1="100%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#A18DC2" />
            <stop offset="50%" stopColor="#BAC3E3" />
            <stop offset="100%" stopColor="#A18DC2" />
          </linearGradient>
          <linearGradient id="ca-shield-grad-2" x1="100%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#CE7EB3" />
            <stop offset="50%" stopColor="#D6A0C8" />
            <stop offset="100%" stopColor="#A090C4" />
          </linearGradient>
        </defs>
        <path
          d="M16 30C11.5 28.6 8 24.8 6.5 20.5C5.6 18.2 5.1 15.7 4.8 13.2C4.8 12.6 4.4 6.4 4.7 6.4C8.7 6.3 12.6 5.3 16 3"
          fill="url(#ca-shield-grad-1)"
        />
        <path
          d="M16 30C20.5 28.6 24 24.8 25.5 20.5C26.4 18.2 26.9 15.7 27.2 13.2C27.2 12.6 27.6 6.4 27.3 6.4C23.3 6.3 19.4 5.3 16 3"
          fill="url(#ca-shield-grad-2)"
        />
        <path
          d="M4.7 6.4C4.7 6.4 7.2 6.4 10 5.7C10 5.7 10.1 18 16 21.5V30C16 30 4.6 27.7 4.6 8C4.6 6.3 4.7 6.4 4.7 6.4Z"
          fill="url(#ca-shield-grad-1)"
          opacity="0.7"
        />
        <path
          d="M27.3 6.4C26.7 6.3 24.9 6.4 22 5.7C22 5.7 21.9 18 16 21.5V30C16 30 27.5 27.4 27.4 8C27.4 7.9 27.4 7.8 27.4 7.7C27.4 6.4 27.3 6.4 27.3 6.4Z"
          fill="url(#ca-shield-grad-2)"
          opacity="0.7"
        />
        <path
          d="M22.5 11.5L22.6 11.7C21.2 13 19.9 14.4 18.7 15.9C17 18.3 15.4 21 14 23.8H13.8L9.8 18.9L9.9 18.7H10L13.4 20.6C14.8 18.7 16.3 16.8 18 15.2C18.8 14.4 19.6 13.7 20.4 13C21.2 12.4 21.9 11.9 22.5 11.5Z"
          fill="white"
        />
      </svg>
      <svg width="40" height="14" viewBox="0 0 80 24" fill="none" aria-hidden className="shrink-0">
        <defs>
          <linearGradient id="ca-text-grad" x1="0%" y1="100%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#A1A6D3" />
            <stop offset="50%" stopColor="#9FA0CF" />
            <stop offset="80%" stopColor="#A097C9" />
            <stop offset="100%" stopColor="#A089C0" />
          </linearGradient>
        </defs>
        <text
          x="0"
          y="20"
          fill="url(#ca-text-grad)"
          fontFamily="'Montserrat', sans-serif"
          fontWeight="600"
          fontSize="20"
          letterSpacing="-0.5"
        >
          RegO
        </text>
      </svg>
    </span>
  );
}

function EmptyState({ onSend }: { onSend: (prompt: string, origin?: AssistantSuggestionOrigin | null) => void }) {
  const reduceMotion = useReducedMotion();
  // Read, never computed here: the store chooses the heading when this state appears so it cannot change
  // under the user's eyes on a re-render or when the hour ticks over. The name is already folded in.
  const greeting = useComplianceAssistantStore((s) => s.greeting);
  const suggestions = useComplianceAssistantStore((s) => s.suggestions);
  const suggestionsLoading = useComplianceAssistantStore((s) => s.suggestionsLoading);
  const suggestionsSource = useComplianceAssistantStore((s) => s.suggestionsSource);
  const unavailableReason = useComplianceAssistantStore((s) => s.suggestionsUnavailableReason);
  const pageAware = useComplianceAssistantStore((s) => s.pageAwareSuggestions);
  const setPageAwareSuggestions = useComplianceAssistantStore((s) => s.setPageAwareSuggestions);
  // The page the rows on screen were made for, not the one the user is on — the two diverge while a
  // request is in flight, and naming the destination under the previous page's questions would mislead.
  // Both are null while off, because that state asks nothing about the page.
  //
  // Three sources, because one is never enough: several pages are more than one page and none of them
  // changes the URL, and some of those pages have a title only they can know — the role dashboard's is
  // computed from the viewer's roles. See `panelPageLabel` for the order.
  const suggestionsRoute = useComplianceAssistantStore((s) => s.suggestionsRoute);
  const suggestionsView = useComplianceAssistantStore((s) => s.suggestionsView);
  const suggestionsPageTitle = useComplianceAssistantStore((s) => s.suggestionsPageTitle);
  const pageLabel = panelPageLabel(suggestionsPageTitle, suggestionsView, suggestionsRoute);
  // One definition of what each state looks like, so the places that need it cannot drift into three
  // greens and a blue.
  //
  // The ON border carries more weight than the OFF one on purpose. Following the page is the state the
  // panel wants to advertise, and at the OFF weight the green was invisible against the panel surface —
  // the only green on screen was the word "On", which read as a stray accent rather than as the outline
  // of the thing it labels.
  const accent = pageAware
    ? {
        border: "border-emerald-500/60 dark:border-emerald-400/50",
        text: "text-emerald-600 dark:text-emerald-400",
        // Overrides the theme's `bg-primary` on the checked track, which is indigo. Local to this one
        // switch: every other switch in the app should stay on the theme colour.
        switchTrack:
          "data-[state=checked]:bg-emerald-500 dark:data-[state=checked]:bg-emerald-400",
      }
    : {
        border: "border-sky-500/30 dark:border-sky-400/25",
        text: "text-sky-600 dark:text-sky-400",
        switchTrack: "",
      };

  const container = reduceMotion
    ? undefined
    : {
        hidden: {},
        show: { transition: { staggerChildren: 0.045, delayChildren: 0.05 } },
      };
  const item = reduceMotion
    ? undefined
    : {
        hidden: { opacity: 0, y: 10 },
        show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] as const } },
      };

  // Held here rather than written inline, because the two states put them in different places: inside
  // the outlined box while it is following the page, and below it while it is off. Off's box is about
  // the control alone — the questions under it are the application's, not that box's, and enclosing
  // them made the outline look like it was scoping them to the setting.
  const notice = unavailableReason ? (
    // Above the rows rather than instead of them: the caller cannot use this page's module, but the
    // rows are still places they can go.
    <p className="mt-2.5 rounded-lg border border-border/60 bg-muted/40 px-3 py-2 text-[0.75rem] leading-snug text-muted-foreground dark:border-white/10 dark:bg-white/[0.04]">
      {unavailableReason}
    </p>
  ) : null;
  // No `suggestions.length === 0` guard: the freshness check already blocks a same-context refetch, so
  // anything that reaches the network is a different question — and leaving the old rows up would offer
  // the previous page's questions on this one.
  const rows = (
    <div className="mt-2.5 space-y-1.5">
      {suggestionsLoading ? (
        <SuggestionRowSkeleton />
      ) : (
        suggestions.map((suggestion) => (
          <SuggestionChip
            key={suggestion.id}
            variant="row"
            title={suggestion.text}
            onClick={() =>
              onSend(
                suggestion.text,
                // No source means these are the local fallback, so there is nothing for the backend to
                // attribute a click to.
                suggestionsSource
                  ? { id: suggestion.id, kind: suggestion.kind, source: suggestionsSource }
                  : null,
              )
            }
          />
        ))
      )}
    </div>
  );

  return (
    <motion.div
      variants={container}
      initial={reduceMotion ? undefined : "hidden"}
      animate={reduceMotion ? undefined : "show"}
      className="flex flex-col items-center px-2 pt-6 text-center"
    >
      <motion.div variants={item} className="relative flex justify-center">
        <motion.span
          aria-hidden
          initial={false}
          className="pointer-events-none absolute left-1/2 top-1/2 size-24 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(139,92,246,0.45),transparent_70%)] blur-xl"
          animate={reduceMotion ? {} : { opacity: [0.3, 0.6, 0.3], scale: [0.9, 1.08, 0.9] }}
          transition={reduceMotion ? undefined : { duration: 3.6, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="group relative cursor-pointer"
          whileHover={reduceMotion ? undefined : { scale: 1.1 }}
          whileTap={reduceMotion ? undefined : { scale: 0.95 }}
        >
          <AssistantMascot className="size-16 drop-shadow-[0_6px_16px_rgba(124,58,237,0.5)] transition-[filter] duration-300 group-hover:drop-shadow-[0_10px_26px_rgba(139,92,246,0.72)]" />
        </motion.div>
      </motion.div>

      {/* No product name here: the panel header two lines above already says "Compliance Companion", so
          repeating it would spend the largest text on screen saying nothing new. */}
      {/* Two deliberate lines rather than one wrapping sentence. Joined, it broke wherever the panel's
          narrow column ran out — stranding the dash at the end of a line — and the break moved with the
          user's name and the rotating question. Splitting it makes every variant wrap the same way. */}
      <motion.h2
        variants={item}
        className="mt-3 max-w-[20rem] text-balance text-base leading-snug tracking-tight text-foreground"
      >
        <span className="block font-bold">{(greeting ?? DEFAULT_GREETING).lead}</span>
        <span className="mt-0.5 block font-semibold text-foreground/85">
          {(greeting ?? DEFAULT_GREETING).question}
        </span>
      </motion.h2>
      {/* The areas the assistant covers, as chips rather than a sentence: the prose version always wrapped
          onto a second line ending in one orphaned word, and a list of four nouns reads faster as four
          items than as a comma-separated clause.

          @container on the wrapper means the layout responds to the *panel's* width, not the viewport —
          the docked rail (~432px usable) and the compact floating card (~352px) are both fixed widths that
          no breakpoint corresponds to. One row in the rail, 2×2 in the card. */}
      <motion.div variants={item} className="@container mt-3 w-full">
        <p className="mb-2 text-[0.6875rem] font-bold tracking-wider text-muted-foreground">
          Ask about
        </p>
        {/* Dividers are a left border on every item except the first in its row, so nothing ever trails at
            a line break: 2 columns by default, 4 once the container is wide enough. */}
        {/* 11px matches the "Try asking" label below, and it is what makes four columns fit: at 13px the
            longest label ("Assessments") needs more than a quarter of the panel's width and would wrap
            inside its own cell. */}
        <div className="grid grid-cols-2 gap-y-2 @[26rem]:grid-cols-4">
          {SCOPE_AREAS.map(({ icon: Icon, label }, index) => (
            <div
              key={label}
              className={cn(
                "flex items-center justify-center gap-1 px-1 text-[0.6875rem] font-semibold text-foreground/85",
                // Every second item starts a new row in the 2-column layout, so it gets no left border.
                index % 2 === 1 && "border-l border-border/70 dark:border-white/10",
                // At 4 columns only the very first item leads a row, so every other item gets one.
                index % 2 === 0 &&
                  index > 0 &&
                  "@[26rem]:border-l @[26rem]:border-border/70 @[26rem]:dark:border-white/10",
              )}
            >
              <Icon className="size-3.5 shrink-0 text-primary" strokeWidth={2} aria-hidden />
              {label}
            </div>
          ))}
        </div>
      </motion.div>

      <motion.div variants={item} className="mt-6 w-full text-left">
        {/* On, the outline holds the whole feature: the switch and the questions it governs belong
            together. Off, it closes under the switch — see `rows` above. Green following, blue off, so
            the two states differ by more than a word.

            One box in both states rather than a branch per state: rendering two of them would remount
            the switch on every flip, which drops keyboard focus mid-toggle.

            Outline only. A tint would sit behind the suggestion rows, which carry their own surface,
            and two stacked translucent fills muddy both. */}
        <div className={cn("rounded-xl border p-2.5 transition-colors", accent.border)}>
          {/* The mode reads off the panel, never out of a tooltip: whether the assistant is following
              the page is the only thing that explains why these rows did or did not change. */}
          <label className="block cursor-pointer px-0.5">
            <span className="flex items-center gap-2">
              <Sparkles className={cn("size-3.5 shrink-0", accent.text)} aria-hidden />
              <span className="flex-1 select-none text-[0.8125rem] font-semibold leading-snug text-foreground">
                {pageAware ? "Suggestions based on current page" : "Page-Aware Suggestions"}
              </span>
              <Switch
                checked={pageAware}
                onCheckedChange={setPageAwareSuggestions}
                aria-label="Page-aware suggestions"
                className={accent.switchTrack}
              />
              {/* Sized for the longer word, not the shorter: at a fixed 1.5rem "OFF" wrapped onto two
                  lines. A minimum rather than a fixed width still keeps the switch from shifting. */}
              <span
                className={cn(
                  "min-w-[1.75rem] select-none whitespace-nowrap text-right text-[0.6875rem] font-bold uppercase tracking-wider",
                  pageAware ? accent.text : "text-muted-foreground",
                )}
              >
                {pageAware ? "On" : "Off"}
              </span>
            </span>
            <span className="mt-1 block select-none text-[0.6875rem] leading-snug text-muted-foreground">
              {/* Off names the control rather than describing itself, because the rows below it are the
                  general ones and the only thing this state cannot show the user is what turning it on
                  would give them. */}
              {pageAware
                ? "I'll update suggestions whenever you navigate to a different page."
                : "To get suggestions based on the page you're on."}
            </span>
          </label>

          {/* What the rows are grounded in — which is a thing to say only while there is a page grounding
              them. Off asks the application-wide question, so there is no detected page to report and
              nothing here would be true. */}
          {pageAware && (
            <div className={cn("mt-2.5 rounded-lg border px-3 py-2", accent.border)}>
              <p className="flex items-center gap-1.5 text-[0.75rem] font-semibold leading-snug text-foreground">
                <FileText className={cn("size-3.5 shrink-0", accent.text)} aria-hidden />
                {pageLabel ? `Page detected: ${pageLabel}` : "Page detected"}
              </p>
              <p className="mt-0.5 text-[0.6875rem] leading-snug text-muted-foreground">
                Here are some questions you can ask on this page.
              </p>
            </div>
          )}

          {pageAware && notice}
          {pageAware && rows}

          {/* The panel's own reasoning, under the rows it explains — so a user who wonders why these
              changed on a new page has the answer beneath them. Off has no such question to answer: the
              rows are the same everywhere, and the subtitle above has already said why. */}
          {pageAware && suggestions.length > 0 && !suggestionsLoading && (
            <div className={cn("mt-2.5 rounded-lg border border-dashed px-3 py-2", accent.border)}>
              <p className="flex items-center gap-1.5 text-[0.6875rem] font-semibold text-foreground">
                <Lightbulb className={cn("size-3.5 shrink-0", accent.text)} aria-hidden />
                Why these suggestions?
              </p>
              <p className="mt-0.5 text-[0.6875rem] leading-snug text-muted-foreground">
                {`These are recommended based on ${pageLabel ?? "the page you are on"}. I'll update them when you move to another page.`}
              </p>
            </div>
          )}
        </div>

        {/* Off only: outside the box, because these questions are the application's rather than that
            switch's. */}
        {!pageAware && notice}
        {!pageAware && rows}
      </motion.div>
    </motion.div>
  );
}

export function ChatPanel({ dragControls }: { dragControls?: DragControls }) {
  const open = useComplianceAssistantStore((s) => s.open);
  const minimized = useComplianceAssistantStore((s) => s.minimized);
  const messages = useComplianceAssistantStore((s) => s.messages);
  const isThinking = useComplianceAssistantStore((s) => s.isThinking);
  const sendMessage = useComplianceAssistantStore((s) => s.sendMessage);
  const stopGeneration = useComplianceAssistantStore((s) => s.stopGeneration);
  const retry = useComplianceAssistantStore((s) => s.retry);
  const resetConversation = useComplianceAssistantStore((s) => s.resetConversation);
  const historyContext = useComplianceAssistantStore((s) => s.historyContext);
  const conversations = useComplianceAssistantStore((s) => s.conversations);
  const historyLoading = useComplianceAssistantStore((s) => s.historyLoading);
  const historyListError = useComplianceAssistantStore((s) => s.historyListError);
  const pinnedIds = useComplianceAssistantStore((s) => s.pinnedIds);
  const togglePin = useComplianceAssistantStore((s) => s.togglePin);
  const deleteConversation = useComplianceAssistantStore((s) => s.deleteConversation);
  const historyError = useComplianceAssistantStore((s) => s.historyError);
  const clearHistoryError = useComplianceAssistantStore((s) => s.clearHistoryError);
  const loadConversations = useComplianceAssistantStore((s) => s.loadConversations);
  const loadSuggestions = useComplianceAssistantStore((s) => s.loadSuggestions);
  const setCurrentRoute = useComplianceAssistantStore((s) => s.setCurrentRoute);
  const openConversation = useComplianceAssistantStore((s) => s.openConversation);
  const closePanel = useComplianceAssistantStore((s) => s.closePanel);
  const hidePanel = useComplianceAssistantStore((s) => s.hidePanel);
  const minimizePanel = useComplianceAssistantStore((s) => s.minimizePanel);
  const maximizePanel = useComplianceAssistantStore((s) => s.maximizePanel);
  const conversationId = useComplianceAssistantStore((s) => s.conversationId);

  const [input, setInput] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const stuckRef = useRef(true);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const prevCountRef = useRef(0);
  const initializedRef = useRef(false);
  const convIdRef = useRef(conversationId);
  const hasConvoRef = useRef(false);
  const reduceMotion = useReducedMotion();

  const scrollToBottom = useCallback((behavior: ScrollBehavior = "auto") => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
    stuckRef.current = true;
    setIsAtBottom(true);
  }, []);

  const hasConversation = messages.length > 0;
  hasConvoRef.current = hasConversation;

  // This component sits under MainLayout and therefore inside RouterProvider, which is why the route can
  // be read here — the zustand store lives outside the component tree and cannot use router hooks. It is
  // mounted for both panel forms (the docked rail via CompanionWorkspace and the minimized card via
  // ComplianceAssistant), so one host covers every open state.
  const { pathname } = useLocation();

  // Publish the live route. Deliberately NOT gated on hasConversation: mid-conversation is exactly when
  // the suggestions effect below stops running, and it is also when the chat most needs the current page —
  // "summarise this" has to mean the page in view, not the one the panel was opened on.
  useEffect(() => {
    if (!open) return;
    setCurrentRoute(pathname);
  }, [open, pathname, setCurrentRoute]);

  // Suggestions are the empty state only, so this one stays gated. Keyed on the selected record as well as
  // the pathname: the OSCAL authoring pages leave the URL on the list path when a row is opened, so
  // pathname alone never changes and the panel would keep showing list-level questions. The store reads the
  // record itself — this subscription exists purely to re-run the effect.
  const selectedItemId = useUiContextStore((s) => s.selectedItem?.id ?? null);
  // And on the view, for the same reason one level deeper: moving from a catalog's header into its
  // control-groups tree changes neither the pathname nor the selected record.
  const view = useUiContextStore((s) => s.view);
  // emptyStateVisit too: it advances on each open and on "New chat", and it is what rotates the question
  // window. Without it here, a repeat visit would keep the previous visit's rows.
  const emptyStateVisit = useComplianceAssistantStore((s) => s.emptyStateVisit);
  // And on the preference itself, so switching it back on picks up the page the user has since moved
  // to. The store decides whether that turns into a request; this only re-runs the effect.
  const pageAware = useComplianceAssistantStore((s) => s.pageAwareSuggestions);
  useEffect(() => {
    if (!open || hasConversation) return;
    void loadSuggestions(pathname);
  }, [open, hasConversation, pathname, selectedItemId, view, emptyStateVisit, pageAware, loadSuggestions]);

  const liveRegionText = useMemo(() => {
    if (isThinking) return "Compliance Companion is responding";
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.role !== "assistant") continue;
      if (!m.text) return "";
      return m.text
        .replace(/```[\s\S]*?```/g, " chart. ")
        .replace(/\[([^\]]+)]\([^)]*\)/g, "$1")
        .replace(/[#*_`>|]/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    }
    return "";
  }, [isThinking, messages]);

  // Do NOT add `overflow-anchor: none` - native scroll anchoring stops content jumping.
  // rAF-batch the follow write, or ResizeObserver fires several times per frame and it jitters.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const FOLLOW_PX = 8;
    const BUTTON_PX = 80;
    let lastTop = el.scrollTop;
    let raf = 0;
    let scrollRaf = 0;

    const readScroll = () => {
      scrollRaf = 0;
      const top = el.scrollTop;
      const d = el.scrollHeight - top - el.clientHeight;
      // `d > FOLLOW_PX`: a shrinking container clamps scrollTop down, reading as an up-scroll.
      if (top < lastTop - 1 && d > FOLLOW_PX) {
        stuckRef.current = false;
      } else if (d <= FOLLOW_PX) {
        stuckRef.current = true;
      }
      lastTop = top;
      setIsAtBottom((prev) => {
        const atBottom = d <= BUTTON_PX;
        return prev === atBottom ? prev : atBottom;
      });
    };

    const onScroll = () => {
      if (!scrollRaf) scrollRaf = requestAnimationFrame(readScroll);
    };

    el.addEventListener("scroll", onScroll, { passive: true });

    let ro: ResizeObserver | undefined;
    const content = contentRef.current;
    if (content && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => {
        if (!stuckRef.current || !hasConvoRef.current || raf) return;
        raf = requestAnimationFrame(() => {
          raf = 0;
          if (!stuckRef.current || !hasConvoRef.current) return;
          el.scrollTop = el.scrollHeight;
          lastTop = el.scrollTop;
        });
      });
      ro.observe(content);
    }

    return () => {
      el.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
      if (scrollRaf) cancelAnimationFrame(scrollRaf);
      ro?.disconnect();
    };
  }, []);

  // The compact card is portaled to <body>, so the wheel must be forwarded to the page by hand.
  useEffect(() => {
    if (!minimized) return;
    const el = scrollRef.current;
    if (!el) return;

    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
      const dy = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
      const canScrollHere = el.scrollHeight > el.clientHeight + 1;
      const atTop = el.scrollTop <= 0;
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
      if (canScrollHere && !(dy < 0 && atTop) && !(dy > 0 && atBottom)) return;

      const page = document.querySelector<HTMLElement>("[data-app-scroll]");
      if (!page || page.scrollHeight <= page.clientHeight + 1) return;
      page.scrollTop += dy;
      e.preventDefault();
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [minimized]);

  useEffect(() => {
    if (convIdRef.current !== conversationId) {
      convIdRef.current = conversationId;
      initializedRef.current = false;
      prevCountRef.current = 0;
    }
    if (!hasConversation) {
      prevCountRef.current = 0;
      initializedRef.current = false;
      if (scrollRef.current) scrollRef.current.scrollTop = 0;
      return;
    }
    if (!initializedRef.current) {
      initializedRef.current = true;
      prevCountRef.current = messages.length;
      scrollToBottom("auto");
      return;
    }
    if (messages.length > prevCountRef.current) {
      const last = messages[messages.length - 1];
      if (last?.role === "user") scrollToBottom(reduceMotion ? "auto" : "smooth");
    }
    prevCountRef.current = messages.length;
  }, [messages, hasConversation, conversationId, scrollToBottom, reduceMotion]);

  useEffect(() => {
    if (open && !minimized) {
      textareaRef.current?.focus();
    }
  }, [open, minimized]);

  const resetTextareaHeight = () => {
    if (textareaRef.current) textareaRef.current.style.height = "auto";
  };

  const handleSend = () => {
    const value = input.trim();
    if (!value || isThinking || value.length > MAX_CHARS) return;
    sendMessage(value);
    setInput("");
    resetTextareaHeight();
  };

  // MUST stay stable, or memo(MessageBubble) re-renders every bubble.
  // Must stay referentially stable: MessageBubble is memo-ised and takes this as `onSend`, so a new
  // function each render would re-render every bubble in the transcript. The optional origin rides along
  // rather than becoming a second prop for the same reason.
  const handleQuickSend = useCallback(
      (prompt: string, origin?: AssistantSuggestionOrigin | null) => sendMessage(prompt, origin),
      [sendMessage],
  );
  const handleRetry = useCallback((msg: ChatMessage) => retry(msg.id), [retry]);

  const showHistory = useCallback(() => {
    setHistoryOpen(true);
    void loadConversations();
  }, [loadConversations]);

  const handleSelectConversation = useCallback(
    async (id: string) => {
      if (await openConversation(id)) setHistoryOpen(false);
    },
    [openConversation],
  );

  const transcript = useMemo(() => buildTranscriptItems(messages, Date.now()), [messages]);
  const lastMessageId = messages[messages.length - 1]?.id;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // isComposing guard: Enter would commit an in-progress IME candidate.
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleSend();
    }
  };

  const overLimit = input.length > MAX_CHARS;
  const nearLimit = input.length > MAX_CHARS * 0.9;
  const canSend = input.trim().length > 0 && !isThinking && !overLimit;

  return (
    <div className="relative flex h-full flex-col overflow-hidden bg-white dark:bg-gradient-to-b dark:from-[#211c42] dark:via-[#171430] dark:to-[#110f22]">
      <div
        onPointerDown={(e) => {
          if (!minimized) return;
          if ((e.target as HTMLElement).closest("button")) return;
          dragControls?.start(e);
        }}
        className={cn(
          "relative flex min-h-[3.75rem] shrink-0 items-center justify-between gap-2 border-b border-white/20 bg-gradient-to-br from-indigo-600 via-violet-600 to-violet-500 px-4 py-3 dark:border-white/10 dark:from-indigo-600/85 dark:via-violet-600/80 dark:to-fuchsia-600/65",
          minimized && "cursor-grab touch-none select-none active:cursor-grabbing",
        )}
      >
        <span
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent"
        />
        <div className="relative flex min-w-0 flex-1 items-center gap-2.5">
          <AssistantMascot
            className="size-9 shrink-0 drop-shadow-[0_2px_4px_rgba(0,0,0,0.25)]"
            inspect={isThinking}
            hoverInspect={false}
          />
          <div className="min-w-0">
            <p className="truncate text-[0.875rem] font-bold leading-tight text-white">
              Compliance Companion
            </p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-0.5">
          <HeaderButton label="Chat history" onClick={showHistory}>
            <History className="size-4" />
          </HeaderButton>
          {hasConversation && (
            <HeaderButton label="Reset conversation" onClick={resetConversation}>
              <RotateCcw className="size-4" />
            </HeaderButton>
          )}
          {minimized ? (
            <HeaderButton label="Expand" onClick={maximizePanel}>
              <Maximize2 className="size-4" />
            </HeaderButton>
          ) : (
            <HeaderButton label="Compact" onClick={minimizePanel}>
              <Minimize2 className="size-4" />
            </HeaderButton>
          )}
          <HeaderButton label="Minimize" onClick={hidePanel}>
            <Minus className="size-4" />
          </HeaderButton>
          <HeaderButton label="Close" onClick={closePanel}>
            <X className="size-4" />
          </HeaderButton>
        </div>
      </div>

      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {liveRegionText}
      </div>

      {historyContext && (
        <HistoryBanner context={historyContext} onNewChat={resetConversation} />
      )}

      <div className="relative min-h-0 flex-1">
        <div
          ref={scrollRef}
          className="scrollbar-neutral h-full overflow-y-auto [overscroll-behavior:contain]"
        >
          <div ref={contentRef} className="space-y-6 px-4 pb-8 pt-5">
            {hasConversation ? (
              <>
                {transcript.map((item) =>
                  item.kind === "separator" ? (
                    <TranscriptSeparator
                      key={item.id}
                      label={item.label}
                      sublabel={item.sublabel}
                      emphasis={item.emphasis}
                    />
                  ) : (
                    <MessageBubble
                      key={item.message.id}
                      message={item.message}
                      isLast={item.message.id === lastMessageId}
                      onSend={handleQuickSend}
                      onRetry={handleRetry}
                    />
                  ),
                )}
                <AnimatePresence>
                  {isThinking && <TypingIndicator key="ca-typing" />}
                </AnimatePresence>
              </>
            ) : (
              <EmptyState onSend={handleQuickSend} />
            )}
          </div>
        </div>
        {/* Must stay a static overlay, NOT a mask-image on the scroller (mask drops it off the compositor). */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 h-5 bg-gradient-to-b from-transparent to-white dark:to-[#131028]"
        />
      </div>

      <div className="relative shrink-0 px-3 pb-3 pt-1">
        <AnimatePresence>
          {hasConversation && !isAtBottom && (
            <motion.button
              type="button"
              onClick={() => scrollToBottom(reduceMotion ? "auto" : "smooth")}
              initial={{ opacity: 0, y: 6, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 6, scale: 0.9 }}
              transition={{ duration: 0.16, ease: "easeOut" }}
              aria-label="Scroll to latest message"
              className="absolute left-1/2 top-0 z-10 flex size-8 -translate-x-1/2 -translate-y-[calc(100%+0.375rem)] items-center justify-center rounded-full border border-border/70 bg-card text-foreground shadow-md transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background dark:border-white/10 dark:bg-[#26224a]"
            >
              <ChevronDown className="size-4" />
            </motion.button>
          )}
        </AnimatePresence>
        <div
          className={cn(
            "relative flex items-end rounded-2xl border shadow-sm transition-all duration-200",
            // Opaque fill, NOT backdrop-blur (blur repainted every scroll frame).
            "border-border/70 bg-card/95",
            "focus-within:border-primary/50 focus-within:shadow-md focus-within:ring-4 focus-within:ring-primary/10",
            "dark:border-white/10 dark:bg-white/[0.07] dark:focus-within:border-violet-300/40",
            overLimit &&
              "border-red-400/70 focus-within:border-red-400/70 focus-within:ring-red-500/10 dark:border-red-400/60",
          )}
        >
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onInput={(e) => {
              const t = e.currentTarget;
              t.style.height = "auto";
              t.style.height = `${Math.min(t.scrollHeight, 140)}px`;
            }}
            rows={1}
            maxLength={MAX_CHARS}
            aria-label="Message the Compliance Companion"
            placeholder="Ask a compliance question"
            /* text-base on mobile stops iOS Safari auto-zooming on focus. */
            className="max-h-[140px] min-h-[44px] w-full resize-none border-none bg-transparent py-3 pl-4 pr-14 text-base leading-relaxed text-foreground outline-none placeholder:text-muted-foreground focus:ring-0 sm:text-[0.8125rem]"
          />
          <div className="absolute bottom-0 right-2 flex h-[44px] items-center">
            <motion.button
              type="button"
              onClick={isThinking ? stopGeneration : handleSend}
              disabled={isThinking ? false : !canSend}
              aria-label={isThinking ? "Stop generating" : "Send message"}
              title={
                isThinking
                  ? "Stop generating"
                  : overLimit
                    ? "Message exceeds the character limit"
                    : "Send message"
              }
              whileHover={isThinking || canSend ? { scale: 1.06 } : undefined}
              whileTap={isThinking || canSend ? { scale: 0.9 } : undefined}
              transition={{ type: "spring", stiffness: 420, damping: 17 }}
              className="group/send flex size-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-600 via-violet-600 to-violet-500 text-white shadow-[0_4px_12px_-3px_rgba(124,58,237,0.55)] transition-shadow hover:shadow-[0_6px_16px_-3px_rgba(139,92,246,0.6)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:from-slate-300 disabled:via-slate-300 disabled:to-slate-300 disabled:text-white/70 disabled:shadow-none dark:disabled:from-white/10 dark:disabled:via-white/10 dark:disabled:to-white/10 dark:disabled:text-white/40"
            >
              {isThinking ? (
                <Square className="size-3.5 fill-current" />
              ) : (
                <Send className="size-4 transition-transform duration-200 group-hover/send:-translate-y-0.5 group-hover/send:translate-x-0.5" />
              )}
            </motion.button>
          </div>
        </div>
        <div className="relative mt-2 flex items-center justify-center px-1">
          <PoweredByRego />
          {nearLimit && (
            <span
              className={cn(
                "absolute right-1 shrink-0 text-[0.6875rem] font-medium tabular-nums",
                overLimit ? "text-red-500" : "text-muted-foreground",
              )}
            >
              {input.length}/{MAX_CHARS}
            </span>
          )}
        </div>
      </div>

      <AnimatePresence>
        {historyOpen && (
          <HistoryPanel
            key="ca-history"
            conversations={conversations}
            loading={historyLoading}
            error={historyListError}
            onRetry={() => void loadConversations()}
            pinnedIds={pinnedIds}
            activeId={conversationId}
            actionError={historyError}
            onDismissActionError={clearHistoryError}
            onSelect={(id) => void handleSelectConversation(id)}
            onTogglePin={togglePin}
            onDelete={(id) => void deleteConversation(id)}
            onClose={() => setHistoryOpen(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
